/**
 * La SCALA 40 dal BFF di Limulo: le sei rotte `/v1/games/scala40/*` (`limulo-app/rotte.js` di QScala40), iniettate in `limuloApi` come `services/limuloScopa.ts` (stessa base, stessa sessione, stesso refresh),
 * e NON dentro `services/limuloApi.ts`: la zona della scala 40 resta fuori dal file di tutti. Le forme sono in `lib/gamehost/rest.ts` e `lib/gamehost/ticket.ts` (il trasporto comune, ADR-0235 §4), qui se ne passa
 * il dominio; i consumatori sono la Hub (`components/scala40/*`) e l'hook della partita (`hooks/useScala40Match.ts`).
 *
 * 🔴 Le POST non si ritentano da sole (`limuloBaseQuery` non ritenta nessuna POST) e non si mettono in cache; le letture non restano in cache oltre la schermata (`keepUnusedDataFor: 0`): un tavolo finito non deve
 * ricomparire dalla cache.
 *
 * `/*#__PURE__*\/` e NIENTE destrutturazione degli hook: i consumatori usano `scala40Api.useXxx()` dentro il loro corpo. Cosi, se nessun consumatore sopravvive alla build (la produzione: il flag piega via la Hub e
 * le rotte), il bundler toglie anche questo modulo, e con lui le definizioni delle rotte.
 */
import { limuloApi } from '@/services/limuloApi';
import { corpoDi, entraBody, ingressoOf, mescolataOf, restDi, rivincitaBody, tavoliMieiOf, tavoloCreatoOf } from '@/lib/gamehost/rest';
import type { TavoliMiei, TavoloCreato } from '@/lib/gamehost/rest';
import { ticketBody, ticketGrantOf, ticketRoute } from '@/lib/gamehost/ticket';
import type { TicketGrant } from '@/lib/gamehost/ticket';
import { POSTI_AMMESSI, SCALA40_DOMINIO, creaTavoloScala40Body } from '@/lib/scala40/gioco';
import type { RichiestaTavoloScala40 } from '@/lib/scala40/gioco';

const REST = restDi(SCALA40_DOMINIO);
const TICKET_ROUTE = ticketRoute(SCALA40_DOMINIO);

export const scala40Api = /*#__PURE__*/ limuloApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `scala40.table.mine.list` — i tavoli VIVI di chi chiama («I miei tavoli / Riprendi»). */
    getMyScala40Tables: builder.query<TavoliMiei, void>({
      query: () => ({ url: REST.tavoliMiei }),
      transformResponse: (response: unknown) => tavoliMieiOf(response, POSTI_AMMESSI),
      keepUnusedDataFor: 0,
    }),
    /** `scala40.table.create` — «Gioca» (casual) e «Invita un amico» (privato), da 2 a 6 posti. */
    createScala40Table: builder.mutation<TavoloCreato | null, RichiestaTavoloScala40>({
      query: (a) => ({ url: REST.tavoli, method: 'POST', body: creaTavoloScala40Body(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(response),
    }),
    /** `scala40.table.join` — «Entra con un codice»: il posto lo sceglie il game host. */
    joinScala40Table: builder.mutation<{ tableId: string } | null, { tableId: string; invito: string; idempotencyKey: string }>({
      query: (a) => ({ url: REST.posti(a.tableId), method: 'POST', body: entraBody(a) }),
      transformResponse: (response: unknown, _meta, a) => ingressoOf(response, a.tableId),
    }),
    /** `scala40.table.rematch` — la rivincita di un tavolo FINITO: un tavolo nuovo (201) o quello gia aperto da un altro (200). */
    rematchScala40Table: builder.mutation<TavoloCreato | null, { tableId: string; idempotencyKey: string }>({
      query: (a) => ({ url: REST.rivincita(a.tableId), method: 'POST', body: rivincitaBody(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(response),
    }),
    /** `scala40.table.mescolata.read` — il record di una smazzata finita (`qlash-fair-v1`). La verifica a schermo non e in questa PR: si legge e basta. */
    getScala40Mescolata: builder.query<Record<string, unknown> | null, { tableId: string; smazzata: number }>({
      query: (a) => ({ url: REST.mescolata(a.tableId, a.smazzata) }),
      transformResponse: (response: unknown) => mescolataOf(response),
      keepUnusedDataFor: 0,
    }),
    /** `scala40.session.ticket.issue` — il ticket MONOUSO (30 s) del socket del tavolo: corpo `{ tableId }` (lo scopo lo compone la rotta del kit). Chi lo chiede lo consuma subito. */
    issueScala40Ticket: builder.mutation<TicketGrant | null, { tableId: string }>({
      query: ({ tableId }) => ({ url: TICKET_ROUTE, method: 'POST', body: ticketBody(tableId) }),
      transformResponse: (response: unknown) => ticketGrantOf(corpoDi(response)),
    }),
  }),
});
